/**
 * Сторінка «Фінансовий результат» — перебудована за дизайн-концепцією
 * (Прогноз/EMS Box): дизайн-токени, Urbanist, Lucide, скляні плитки, світло/
 * темна, колірна догма. Один консолідований запит /financial/result/overview.
 *
 * Блоки ТЗ: топ-4 KPI (+Δ%) → Плановий дохід → Небаланси → заглушки
 * «Втрачена вигода» і «Фінансовий результат». Керування (станція · період
 * День/Місяць/Власний · дата/діапазон · «Факт. розрахунок») — у топбарі.
 * Прапорець VITE_FEATURE_FINANCIAL_RESULT.
 */
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import { Construction } from 'lucide-react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import { getStoredUser } from '@/hooks/useAuth';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useFinancialResultUi } from '@/hooks/useFinancialResultUi';
import type { OverviewPayload } from './overviewTypes';
import FinKpiRow from './FinKpiRow';
import PlannedIncomeBlock from './PlannedIncomeBlock';
import ImbalanceBlock from './ImbalanceBlock';
import ActualSettlementDrawer from './ActualSettlementDrawer';

export default function FinancialResultPage() {
  const { t } = useTranslation();
  const canEdit = useMemo(() => {
    const role = getStoredUser()?.role;
    return role === 'Оператор' || role === 'CTO' || role === 'Фінансовий директор';
  }, []);

  const { selectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id ?? '';
  const { period, date, month, from, to, setWCode, entryNonce } = useFinancialResultUi();

  const [ov, setOv] = useState<OverviewPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);

  const load = useCallback(() => {
    if (!assetId) { setOv(null); return; }
    const q = period === 'day' ? `period=day&date=${date}`
      : period === 'month' ? `period=month&month=${month}`
      : `period=custom&from=${from}&to=${to}`;
    setLoading(true);
    authFetch(`${API_BASE_URL}/financial/result/overview?assetId=${encodeURIComponent(assetId)}&${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p: OverviewPayload | null) => { setOv(p); setWCode(p?.wCode ?? null); })
      .catch(() => setOv(null))
      .finally(() => setLoading(false));
  }, [assetId, period, date, month, from, to, setWCode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (entryNonce > 0) setEntryOpen(true); }, [entryNonce]);

  const gran = ov?.gran ?? (period === 'day' ? 'hour' : 'day');

  return (
    <DashboardLayout breadcrumb={[t('ak_fin_finance'), t('ak_fin_financial_result')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1440)}`} style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {!assetId && (
            <div className="tile p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              {t('ak_fin_select_a_station_in_the_header_to_see')}
            </div>
          )}

          {assetId && (
            <div className="flex flex-col gap-6">
              {/* Топ-4 KPI */}
              <FinKpiRow s={ov?.summary ?? null} closed={!!ov?.monthClose?.isClosed} />

              {loading && !ov && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('ak_fin_loading')}</div>}

              {/* Блок «Плановий дохід» */}
              {ov && <PlannedIncomeBlock data={ov.plannedIncome} gran={gran} />}

              {/* Блок «Небаланси» */}
              {ov && (
                <ImbalanceBlock
                  data={ov.imbalance} gran={gran} period={ov.period}
                  monthClose={ov.monthClose} assetId={assetId} canEdit={canEdit}
                  onChanged={load}
                />
              )}

              {/* Заглушки наступного релізу */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <StubBlock title={t('ak_fin_lost_benefit')} text={t('ak_fin_the_indicator_will_be_implemented_in_t')} />
                <StubBlock title={t('ak_fin_financial_result_comprehensive')} text={t('ak_fin_in_the_next_release_planned_and_actual')} />
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedAsset && (
        <ActualSettlementDrawer
          open={entryOpen} assetId={assetId} assetName={selectedAsset.assetName} date={date} canEdit={canEdit}
          onClose={() => setEntryOpen(false)} onSaved={() => { setEntryOpen(false); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function StubBlock({ title, text }: { title: string; text: string }) {
  const { t } = useTranslation();
  return (
    <div className="tile p-4" style={{ opacity: 0.85 }}>
      <div className="flex items-center gap-2 mb-3">
        <Construction size={16} strokeWidth={1.75} style={{ color: 'var(--warn)' }} />
        <h3 className="font-display text-[15px] font-semibold" style={{ color: 'var(--text)' }}>{title}</h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ color: 'var(--warn)', border: '1px solid var(--warn)' }}>{t('ak_fin_next_release')}</span>
      </div>
      <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>{text}</p>
    </div>
  );
}
